/**
 * Mapeamento do locale do app (pt/en/es…, BCP-47 curto do Módulo de Idiomas) para um
 * locale BCP-47 ADEQUADO À FORMATAÇÃO DE NÚMEROS (`Intl.NumberFormat`).
 *
 * Por que existe: alguns códigos do app são "neutros de região" (ex.: `pt`, `en`, `es`) e o
 * `Intl` aplica convenções diferentes conforme a região. Queremos garantir a convenção certa:
 *  - `pt`  → `pt-BR`  (1.234,56)
 *  - `en`  → `en-US`  (1,234.56)
 *  - `es`  → `es-ES`  (1.234,56)
 *
 * REGRA: aqui só muda o FORMATO/locale — NUNCA o conteúdo (símbolos/nomes/preços continuam iguais).
 * Com o Módulo de Idiomas DESLIGADO, o app fica em `pt` → cai no default `pt-BR` (retrocompat total).
 */

/** Locale BCP-47 de número usado quando nada combina (mantém comportamento histórico do site). */
export const DEFAULT_NUMBER_LOCALE = 'pt-BR'

/**
 * Tabela explícita app-locale → BCP-47 de número. Cobre o superset do Payload (`catalog.ts`).
 * Para códigos já regionais (ex.: `zh-CN`) o próprio código serve ao `Intl`.
 */
const NUMBER_LOCALE_BY_APP_LOCALE: Record<string, string> = {
  pt: 'pt-BR',
  en: 'en-US',
  es: 'es-ES',
  it: 'it-IT',
  fr: 'fr-FR',
  de: 'de-DE',
  tr: 'tr-TR',
  nl: 'nl-NL',
  ko: 'ko-KR',
  pl: 'pl-PL',
  id: 'id-ID',
  ja: 'ja-JP',
  'zh-cn': 'zh-CN',
  'zh-tw': 'zh-TW',
  ar: 'ar-SA',
  ru: 'ru-RU',
  hi: 'hi-IN',
  th: 'th-TH',
  vi: 'vi-VN',
}

/**
 * Resolve o BCP-47 de número a partir do locale do app.
 * - `null`/`undefined`/vazio → default (`pt-BR`).
 * - Código conhecido → da tabela.
 * - Código desconhecido mas válido p/ `Intl` → usado como está (tabela simples + fallback Intl).
 * - Inválido → default.
 */
export function resolveNumberLocale(appLocale: string | null | undefined): string {
  const raw = appLocale?.trim()
  if (!raw) return DEFAULT_NUMBER_LOCALE

  const mapped = NUMBER_LOCALE_BY_APP_LOCALE[raw.toLowerCase()]
  if (mapped) return mapped

  // Código fora da tabela: aceita se o Intl o reconhecer; senão volta ao default.
  try {
    if (Intl.NumberFormat.supportedLocalesOf([raw]).length > 0) return raw
  } catch {
    /* BCP-47 malformado → default */
  }
  return DEFAULT_NUMBER_LOCALE
}

/**
 * Locais cujos NÚMEROS de cripto (preço, market cap, volume, %, oferta) seguem o PRÓPRIO locale,
 * em vez do pt-BR fixo. Decisão do dono (2026-07-06): no chinês seguir o padrão CoinDesk —
 * `Intl` com `zh-CN` entrega vírgula de milhar + ponto decimal (`1,234.56`), símbolo `US$` e a
 * notação compacta com as unidades chinesas 万 (10⁴) / 亿 (10⁸) automaticamente.
 *
 * PENDENTE (D10 do plano moeda×idioma, docs/PLANO_MOEDA_POR_IDIOMA_SEO_2026_07_26.md): acrescentar
 * `'en'` e `'ja'` a este conjunto no MESMO momento em que as moedas por idioma forem ativadas
 * (fase F2.5, o «flip»). Motivo: com o preço passando a sair em ¥ no /ja, o número «¥10.554.655»
 * com ponto de milhar brasileiro fica visivelmente estrangeiro no snippet do Google — o mesmo vale
 * para «US$ 64.420,50» no /en. Ficou de fora desta entrega de propósito: mexer aqui altera a
 * formatação de TODAS as telas de cripto em /en e /ja imediatamente (37 arquivos consomem esta
 * política), o que quebraria a garantia de que este deploy sobe sem nenhuma mudança visível.
 * Único ponto de controle: adicione um código aqui para o número daquele idioma seguir o locale.
 */
export const CRYPTO_NUMBER_LOCALE_LOCALES: ReadonlySet<string> = new Set(['zh-CN'])

/**
 * BCP-47 de número para as funções de formatação de CRIPTO. Devolve o locale próprio quando o idioma
 * está em `CRYPTO_NUMBER_LOCALE_LOCALES` (ex.: `zh-CN`); senão o default fixo pt-BR. Usar em SERVER;
 * em componentes client usar o hook `useNumberLocale()`, que aplica esta mesma política.
 */
export function resolveCryptoNumberLocale(appLocale: string | null | undefined): string {
  if (appLocale && CRYPTO_NUMBER_LOCALE_LOCALES.has(appLocale)) return resolveNumberLocale(appLocale)
  return DEFAULT_NUMBER_LOCALE
}
